type SearchableProduct = {
  name?: string | null;
  sku?: string | null;
  brand?: string | null;
  categoryName?: string | null;
  description?: string | null;
  unit?: string | null;
  variants?: string | null;
};

export function normalizeProductSearch(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function tokenizeProductSearch(query: string): string[] {
  return Array.from(new Set(normalizeProductSearch(query).split(" ").filter(Boolean)));
}

export function matchesProductSearch(product: SearchableProduct, query: string): boolean {
  const tokens = tokenizeProductSearch(query);
  if (tokens.length === 0) return true;

  const searchableText = normalizeProductSearch([
    product.name,
    product.sku,
    product.brand,
    product.categoryName,
    product.description,
    product.unit,
    product.variants,
  ].filter(Boolean).join(" "));

  return tokens.every(token => searchableText.includes(token));
}

export function productSearchScore(product: SearchableProduct, query: string): number {
  const normalizedQuery = normalizeProductSearch(query);
  if (!normalizedQuery) return 0;

  const name = normalizeProductSearch(product.name);
  const sku = normalizeProductSearch(product.sku);
  const brand = normalizeProductSearch(product.brand);
  const category = normalizeProductSearch(product.categoryName);

  if (sku === normalizedQuery) return 1000;
  if (name === normalizedQuery) return 900;
  if (name.startsWith(normalizedQuery)) return 800;
  if (sku.startsWith(normalizedQuery)) return 750;

  const tokens = tokenizeProductSearch(query);
  const nameTokenMatches = tokens.filter(token => name.includes(token)).length;
  const skuTokenMatches = tokens.filter(token => sku.includes(token)).length;
  const brandTokenMatches = tokens.filter(token => brand.includes(token)).length;
  const categoryTokenMatches = tokens.filter(token => category.includes(token)).length;

  return nameTokenMatches * 100 + skuTokenMatches * 90 + brandTokenMatches * 50 + categoryTokenMatches * 40;
}

export function sortProductsBySearch<T extends SearchableProduct>(products: T[], query: string): T[] {
  if (!normalizeProductSearch(query)) return products;
  return [...products].sort((a, b) => productSearchScore(b, query) - productSearchScore(a, query));
}